import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { tracker } from '$utils/tracker';
import { UserTypes } from '$types/UserTypes';
import { UserCandidate } from '../../../../types/serverTypes/userDto';
import { ICreateUser } from '../../../legacy/auth/auth.actions';
import { emailSignUp } from '../../../../api/auth.service';
import {
  CandidateBuilder,
  EmailInternalAuthInput,
  EmailInternalAuthOutput,
} from '../candidates.types';
import {
  SuccessMessageEs,
  SuccessMessageEn,
  ErrorMessageEn,
  ErrorMessageEs,
} from '../../../../constants/alerts';
import { errorHandler, successHandler } from '../../../utils';

/** Auth0's codes for a sign-up whose email it already holds (which one depends on the tenant). */
const AUTH0_EMAIL_TAKEN = ['user_exists', 'invalid_signup'];

class CustomFetchQueryError extends Error {
  constructor(
    public status: number,
    public data: unknown,
    public error: string
  ) {
    super();
  }
}

const mapUserData = (
  user: ICreateUser,
  auth0Id: string,
  referrerId: string | null
) => ({
  FirstName: user.firstName,
  LastName: user.lastName,
  Email: user.email,
  Phone: user.phone,
  ResidenceCountry: user.residenceCountry,
  SignUpType: 0,
  ...(referrerId && { referrerId }),
  Auth0Id: auth0Id,
  Score: 0,
  IsInternallyCreated: true,
  TenantId: user.tenantId,
});

export const emailInternalAuth = (builder: CandidateBuilder) =>
  builder.mutation<EmailInternalAuthOutput, EmailInternalAuthInput>({
    async queryFn(arg, _api, _extraOptions, baseQuery) {
      const referralId = localStorage.getItem('referralId');
      const spanish = (arg.lang ?? 'en') === 'es';
      let emailResponse;
      try {
        emailResponse = await emailSignUp(
          arg.user.email,
          arg.user.password,
          'candidate'
        );
      } catch (error: any) {
        // auth0-js rejects with { code, description }. An email Auth0 already holds means the login
        // exists without a profile here; retyping the email to get past it makes a wrong profile.
        const exists = AUTH0_EMAIL_TAKEN.includes(error?.code);
        const notCreated = spanish
          ? ErrorMessageEs.USER_NOT_CREATED
          : ErrorMessageEn.USER_NOT_CREATED;
        let message: string = notCreated;
        if (exists) {
          message = spanish
            ? ErrorMessageEs.EMAIL_ALREADY_HAS_ACCOUNT
            : ErrorMessageEn.EMAIL_ALREADY_HAS_ACCOUNT;
        } else if (error?.description) {
          message = `${notCreated}: ${error.description}`;
        }
        errorHandler('[EmailInternalAuthAction]', message, error, true);
        return {
          error: {
            status: error?.statusCode ?? 'CUSTOM_ERROR',
            error: message,
            data: error,
          } as FetchBaseQueryError,
        };
      }
      const createUserResponse = (await baseQuery({
        url: '/candidates/internal/create',
        method: 'POST',
        body: mapUserData(arg.user, emailResponse.Id ?? '', referralId),
      })) as QueryReturnValue<UserCandidate>;

      if (!createUserResponse.data) {
        errorHandler(
          '[EmailInternalAuthAction]',
          (arg.lang ?? 'en') === 'es'
            ? ErrorMessageEs.USER_NOT_CREATED
            : ErrorMessageEn.USER_NOT_CREATED,
          'Could not create user',
          true
        );
        throw new CustomFetchQueryError(
          500,
          createUserResponse.data,
          'Could not create user'
        );
      }

      tracker.identify(createUserResponse.data.user.Id, {
        name: `${createUserResponse.data.user.FirstName} ${createUserResponse.data.user.LastName}`,
        email: createUserResponse.data.user.Email,
        type: UserTypes.CANDIDATE,
        role: UserTypes.CANDIDATE,
      });

      tracker.emit('User Registered', {
        account_type: 'Candidate',
        register_type: 'email_pass',
        site_version: localStorage.getItem('appVersion') ?? arg.siteVersion,
      });

      successHandler(
        (arg.lang ?? 'en') === 'es'
          ? SuccessMessageEs.USER_CREATED
          : SuccessMessageEn.USER_CREATED,
        true
      );

      return createUserResponse;
    },
    invalidatesTags: ['Candidates'],
  });
